import { Inject, Injectable, EventEmitter } from "@angular/core";
import { BehaviorSubject, Observable, of } from "rxjs";
import { HttpParams } from "@angular/common/http";
import { ApiCallerService } from "./api/api-caller.service";
import { ApiAdapterService } from "./api-adapter.service";
import {
  AccountInfo,
  RedirectRequest,
  InteractionStatus,
  SilentRequest,
} from "@azure/msal-browser";
import {
  MsalService,
  MSAL_GUARD_CONFIG,
  MsalGuardConfiguration,
  MsalBroadcastService,
} from "@azure/msal-angular";
import { environment } from "src/environments/environment";
import { catchError, map, filter, take, timeout } from "rxjs/operators";
@Injectable({
  providedIn: "root",
})
export class AuthService {
  public msalAccountInfo$$: BehaviorSubject<AccountInfo | undefined | null>;
  protected isAuthenticatingWithMSAL = false;
  basicInfo: any = {};

  // Track current user scopes and permissions
  private userScopes: string[] = [];
  private hasHelpdeskAccess = false;

  // Event emitter to notify components of authentication state changes
  public authStateChange = new EventEmitter<void>();

  // Event emitter to notify when third-party cookie issues are detected
  public cookieWarningNeeded = new EventEmitter<void>();
  constructor(
    private apiCaller: ApiCallerService,
    private apiAdapter: ApiAdapterService,
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private msalService: MsalService,
    private broadcastService: MsalBroadcastService
  ) {
    this.msalAccountInfo$$ = new BehaviorSubject<
      AccountInfo | undefined | null
    >(undefined);

    // Add helper method to window for debugging authentication issues
    (window as any).clearAuthState = () => {
      console.log("Manual authentication state clear triggered");
      this.clearStaleAuthentication();
    };

    // Add helper method to clear stuck interaction state
    (window as any).clearInteractionState = () => {
      console.log("Manual interaction state clear triggered");
      this.clearInteractionState();
    };

    // Add comprehensive clear method
    (window as any).clearAllAuthState = () => {
      console.log("Comprehensive authentication state clear triggered");
      this.clearInteractionState();
      this.clearStaleAuthentication();
    };

    // Add emergency reset method
    (window as any).emergencyAuthReset = () => {
      console.log("Emergency authentication reset triggered");
      this.emergencyAuthReset();
    };

    // Add quick fix for interaction_in_progress error
    (window as any).fixInteractionError = () => {
      console.log("🔧 Quick fix for interaction_in_progress error");
      this.clearInteractionState();
      // Wait a moment then check if we can proceed
      setTimeout(() => {
        const accounts = this.msalService.instance.getAllAccounts();
        if (accounts.length > 0) {
          console.log("✅ Accounts found after clearing interaction state");
          this.checkAndSetActiveAccount();
          this.authStateChange.emit();
        } else {
          console.log("🔄 No accounts found, you may need to login again");
        }
      }, 1000);
    };

    // Add specific fix for startup status issues
    (window as any).fixStartupIssue = () => {
      console.log("🔧 Fixing MSAL startup status issue");
      this.clearInteractionState();
      // Force MSAL to reinitialize
      setTimeout(() => {
        console.log("🚀 Attempting login after startup fix");
        this.performLogin();
      }, 2000);
    };

    // Add debug helper to check redirect response
    (window as any).checkRedirectResponse = () => {
      console.log("🔍 Manually checking redirect response...");
      this.handleRedirectPromise();
    };

    // Add debug helper to check MSAL cache
    (window as any).debugMSALCache = () => {
      console.log("🔍 MSAL Cache Debug:");
      const accounts = this.msalService.instance.getAllAccounts();
      const activeAccount = this.msalService.instance.getActiveAccount();
      console.log("All accounts:", accounts);
      console.log("Active account:", activeAccount);

      // Check browser storage
      console.log(
        "LocalStorage keys:",
        Object.keys(localStorage).filter((key) => key.includes("msal"))
      );
      console.log(
        "SessionStorage keys:",
        Object.keys(sessionStorage).filter((key) => key.includes("msal"))
      );
    };
  }
  // 当前登录用户信息
  getBasicInfo(): Observable<any> {
    return this.apiAdapter.getBasicInfo();
  }
  //判断是否为support team
  getSupportTeamByUser(id: string): Observable<any> {
    return this.apiAdapter.getSupportTeamByUser(id);
  }

  //判断是否为左侧菜单权限
  getAccessRight(): Observable<any> {
    return this.apiAdapter.getAccessRight();
  }

  //获取头像
  getUserImg(id: string): Observable<any> {
    return this.apiAdapter.getUserPhoto(id);
  }
  init() {}

  checkAndSetActiveAccount() {
    console.log("🔍 Checking and setting active account...");

    let activeAccount = this.msalService.instance.getActiveAccount();
    const allAccounts = this.msalService.instance.getAllAccounts();

    console.log("📊 Account check results:", {
      activeAccount: !!activeAccount,
      allAccountsCount: allAccounts.length,
      accounts: allAccounts.map((acc) => ({
        username: acc.username,
        homeAccountId: acc.homeAccountId,
      })),
    });

    if (activeAccount) {
      console.log("✅ Active account found:", activeAccount.username);
      this.msalAccountInfo$$.next(activeAccount);
    } else if (allAccounts.length > 0) {
      console.log(
        "🔄 No active account, but accounts available. Setting first account as active."
      );
      activeAccount = allAccounts[0];
      this.msalAccountInfo$$.next(activeAccount);
      this.msalService.instance.setActiveAccount(activeAccount);
      console.log("✅ Set active account:", activeAccount.username);
    } else {
      console.log("❌ No accounts found in MSAL cache");
      this.msalAccountInfo$$.next(undefined);

      // Check if we're on a redirect page and should handle the response
      this.handleRedirectPromise();
    }
  }

  private handleRedirectPromise() {
    console.log("🔄 Checking for redirect response...");
    this.msalService.instance
      .handleRedirectPromise()
      .then((response) => {
        if (response) {
          console.log("✅ Redirect response received:", response);
          this.msalAccountInfo$$.next(response.account);
          this.msalService.instance.setActiveAccount(response.account);
          this.authStateChange.emit();
        } else {
          console.log("ℹ️ No redirect response found");
        }
      })
      .catch((error) => {
        console.error("❌ Error handling redirect promise:", error);
      });
  }

  /**
   * Clear stuck authentication state
   */
  clearAuthenticationState() {
    this.clearStaleAuthentication();
  }

  clearStaleAuthentication() {
    console.log("🧹 Clearing stuck authentication state...");
    this.isAuthenticatingWithMSAL = false;

    // Reset internal authentication state
    try {
      // Clear active account
      this.msalService.instance.setActiveAccount(null);
      this.msalAccountInfo$$.next(undefined);
      console.log("✅ MSAL authentication state reset");
    } catch (error) {
      console.warn("⚠️ Failed to reset MSAL state:", error);
    }

    // Aggressively clear all MSAL-related storage
    this.clearAllMSALStorage();

    console.log(
      "✅ Authentication state cleared. You can now try to login again."
    );
  }

  private clearAllMSALStorage() {
    console.log("🗑️ Clearing all MSAL storage...");

    // Clear localStorage
    const localStorageKeys = Object.keys(localStorage);
    let localStorageCleared = 0;
    localStorageKeys.forEach((key) => {
      if (
        key.includes("msal") ||
        key.includes("login.windows.net") ||
        key.includes("login.microsoftonline.com") ||
        key.includes("azure") ||
        key.includes("adal") ||
        key.includes("token")
      ) {
        localStorage.removeItem(key);
        localStorageCleared++;
      }
    });
    console.log(`🗑️ Cleared ${localStorageCleared} localStorage entries`);

    // Clear sessionStorage
    const sessionStorageKeys = Object.keys(sessionStorage);
    let sessionStorageCleared = 0;
    sessionStorageKeys.forEach((key) => {
      if (
        key.includes("msal") ||
        key.includes("login.windows.net") ||
        key.includes("login.microsoftonline.com") ||
        key.includes("azure") ||
        key.includes("adal") ||
        key.includes("token")
      ) {
        sessionStorage.removeItem(key);
        sessionStorageCleared++;
      }
    });
    console.log(`🗑️ Cleared ${sessionStorageCleared} sessionStorage entries`);

    // Force clear browser cache for MSAL
    try {
      // Clear any in-memory MSAL state
      (this.msalService.instance as any).browserStorage?.clear();
      console.log("✅ MSAL browser storage cleared");
    } catch (error) {
      console.log("ℹ️ Could not clear MSAL browser storage:", error);
    }
  }

  /**
   * Force clear stuck interaction state
   */
  clearInteractionState() {
    console.log("🔄 Clearing stuck interaction state...");

    try {
      // Clear all possible MSAL interaction state keys
      const clientId = environment.azureAuthenticationConfig.clientId;
      const authority = environment.azureAuthenticationConfig.authority;

      // Get browser storage instance
      const cacheManager = (this.msalService.instance as any).browserStorage;
      if (cacheManager) {
        // Clear various interaction state keys that MSAL might use
        const possibleKeys = [
          `${clientId}|interaction`,
          `msal.interaction.status`,
          `msal.interaction.in.progress`,
          `${clientId}.interaction.status`,
          `${clientId}.interaction.in.progress`,
          "msal.interaction.status." + clientId,
          "interaction_status_" + clientId,
          "interaction_in_progress_" + clientId,
        ];

        possibleKeys.forEach((key) => {
          try {
            cacheManager.removeItem(key);
            localStorage.removeItem(key);
            sessionStorage.removeItem(key);
          } catch (e) {
            // Ignore individual key removal errors
          }
        });

        // Also try to clear any keys that contain 'interaction'
        try {
          for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.toLowerCase().includes("interaction")) {
              localStorage.removeItem(key);
            }
          }
          for (let i = 0; i < sessionStorage.length; i++) {
            const key = sessionStorage.key(i);
            if (key && key.toLowerCase().includes("interaction")) {
              sessionStorage.removeItem(key);
            }
          }
        } catch (e) {
          console.log("Could not clear interaction keys from storage:", e);
        }

        console.log("✅ MSAL interaction state cleared comprehensively");
      }
    } catch (error) {
      console.log("ℹ️ Could not clear interaction state:", error);
    }

    // Force clear MSAL internal state for startup issues
    try {
      // Try to reset MSAL's internal interaction status
      const msalInstance = this.msalService.instance as any;
      if (msalInstance.browserStorage) {
        // Clear interaction status in MSAL's internal cache
        msalInstance.browserStorage.setInteractionInProgress(false);
      }
    } catch (error) {
      console.log("ℹ️ Could not clear MSAL internal state:", error);
    }

    // Reset our own state
    this.isAuthenticatingWithMSAL = false;
    console.log("✅ Internal authentication state reset");
  }

  login() {
    console.log("🔐 Login method called");
    console.log("📊 Current auth state:", {
      isAuthenticatingWithMSAL: this.isAuthenticatingWithMSAL,
      accountsCount: this.msalService.instance.getAllAccounts().length,
      activeAccount: !!this.msalService.instance.getActiveAccount(),
    });

    // Check if MSAL interaction is already in progress using broadcast service
    this.broadcastService.inProgress$
      .pipe(
        map((status: InteractionStatus) => status),
        take(1)
      )
      .subscribe((currentStatus) => {
        console.log("🔄 Current MSAL interaction status:", currentStatus);

        if (currentStatus !== InteractionStatus.None) {
          console.log(
            "⚠️ MSAL interaction already in progress, waiting for completion..."
          );

          // Special handling for startup status - it might be stuck
          if (currentStatus === InteractionStatus.Startup) {
            console.log(
              "🔄 MSAL stuck in startup status, attempting to clear..."
            );
            this.clearInteractionState();
            // Give it a moment then proceed
            setTimeout(() => {
              console.log(
                "🚀 Proceeding with login after clearing startup state..."
              );
              this.performLogin();
            }, 2000);
            return;
          }

          // Wait for interaction to complete, then try again
          this.broadcastService.inProgress$
            .pipe(
              filter(
                (status: InteractionStatus) => status === InteractionStatus.None
              ),
              take(1),
              timeout(5000) // Reduced timeout to 5 seconds
            )
            .subscribe({
              next: () => {
                console.log(
                  "✅ Previous interaction completed, retrying login..."
                );
                this.performLogin();
              },
              error: (error) => {
                console.log(
                  "⏰ Timeout waiting for interaction to complete, forcing clear..."
                );
                this.clearInteractionState();
                setTimeout(() => this.performLogin(), 1000);
              },
            });
          return;
        }

        // No interaction in progress, proceed with login
        this.performLogin();
      });
  }

  private performLogin() {
    // Clear any stuck interaction state before proceeding
    this.clearInteractionState();

    // Prevent multiple simultaneous authentication calls
    if (this.isAuthenticatingWithMSAL) {
      console.log(
        "⚠️ Authentication already in progress, skipping duplicate call"
      );
      return;
    }

    // Check if there's already an interaction in progress
    const accounts = this.msalService.instance.getAllAccounts();
    if (accounts.length > 0) {
      const activeAccount = this.msalService.instance.getActiveAccount();
      console.log(
        "🔍 Found accounts:",
        accounts.length,
        "Active account:",
        !!activeAccount
      );
      // Verify the account has valid tokens before skipping login
      if (activeAccount) {
        // Try to acquire token silently to verify authentication is valid
        this.msalService.instance
          .acquireTokenSilent({
            scopes: [environment.azureAuthenticationConfig.scopes.openid],
            account: activeAccount,
          })
          .then((result) => {
            console.log(
              "✅ User already authenticated with valid tokens, skipping login call"
            );
            // Emit auth state change to update UI components
            this.authStateChange.emit();
            return;
          })
          .catch((error) => {
            console.log(
              "🔄 Stale authentication detected, clearing and proceeding with login"
            );
            this.clearStaleAuthentication();
            // Continue to login after clearing stale data
            this.proceedWithLogin();
          });
        return;
      }
    }

    console.log("🚀 Proceeding with login...");
    this.proceedWithLogin();
  }

  proceedWithLogin() {
    console.log("🚀 proceedWithLogin called");
    this.isAuthenticatingWithMSAL = true;

    if (this.msalGuardConfig.authRequest) {
      console.log("📋 Using authRequest configuration");
      this.msalService
        .loginRedirect({
          ...this.msalGuardConfig.authRequest,
        } as RedirectRequest)
        .subscribe({
          next: () => {
            console.log("✅ Login redirect successful");
            this.isAuthenticatingWithMSAL = false;
          },
          error: (error) => {
            // Log-in failed
            this.isAuthenticatingWithMSAL = false;
            console.error("❌ Login failed with authRequest:", error);

            // Handle specific interaction in progress error
            if (error.errorCode === "interaction_in_progress") {
              console.log(
                "⚠️ Authentication interaction already in progress, please wait..."
              );
              return;
            }

            // TODO Display some message like "Unable to log in"
            console.error("Failed to login, error: ", error);
          },
        });
    } else {
      console.log("📋 Using basic loginRedirect");
      this.msalService.loginRedirect().subscribe({
        next: (result) => {
          console.log("✅ Login done", result);
          this.isAuthenticatingWithMSAL = false;
        },
        error: (error) => {
          // Log-in failed
          this.isAuthenticatingWithMSAL = false;
          console.error("❌ Basic login failed:", error);

          // Handle specific interaction in progress error
          if (error.errorCode === "interaction_in_progress") {
            console.log(
              "⚠️ Authentication interaction already in progress, please wait..."
            );
            return;
          }

          // TODO Display some message like "Unable to log in"
          console.error("Failed to login, error: ", error);
        },
      });
    }
  }

  /**
   * Request specific scopes for the current user
   */
  requestScopesAsync(scopes: string[]): Observable<boolean> {
    const account = this.msalService.instance.getActiveAccount();
    if (!account) {
      console.error("No active account found for scope request");
      return of(false);
    }

    const silentRequest: SilentRequest = {
      scopes: scopes,
      account: account,
    };

    return new Observable((observer) => {
      this.msalService.acquireTokenSilent(silentRequest).subscribe({
        next: (result) => {
          console.log("Scopes acquired successfully:", result.scopes);
          this.userScopes = result.scopes || [];
          this.hasHelpdeskAccess = this.userScopes.some(
            (scope) =>
              scope.includes("helpdesk") || scope.includes("access_as_user")
          );
          observer.next(true);
          observer.complete();
        },
        error: (error) => {
          console.error("Failed to acquire scopes silently:", error);
          // Try interactive request
          const interactiveRequest: RedirectRequest = {
            scopes: scopes,
            account: account,
          };

          this.msalService.acquireTokenRedirect(interactiveRequest).subscribe({
            next: () => observer.next(true),
            error: (interactiveError) => {
              console.error(
                "Failed to acquire scopes interactively:",
                interactiveError
              );
              observer.next(false);
            },
          });
          observer.complete();
        },
      });
    });
  }

  /**
   * Check if user has specific scope access
   */
  hasScope(scope: string): boolean {
    return this.userScopes.some((userScope) => userScope.includes(scope));
  }

  /**
   * Check if user has helpdesk access
   */
  hasHelpdeskScope(): boolean {
    return this.hasHelpdeskAccess;
  }

  /**
   * Get all available scopes for current user
   */
  getUserScopes(): string[] {
    return [...this.userScopes];
  }

  /**
   * Request helpdesk scopes specifically
   */
  requestHelpdeskAccess(): Observable<boolean> {
    const helpdeskScopes = [
      environment.azureAuthenticationConfig.scopes.helpdesk,
      environment.azureAuthenticationConfig.scopes.userAccess,
    ];
    return this.requestScopesAsync(helpdeskScopes);
  }

  /**
   * Emergency authentication bypass - completely reset MSAL instance
   * Use this when stuck in persistent authentication loops
   */
  emergencyAuthReset() {
    console.log("🚨 EMERGENCY AUTHENTICATION RESET INITIATED");

    try {
      // Step 1: Clear all possible MSAL storage aggressively
      this.clearAllMSALStorage();

      // Step 2: Reset all internal state
      this.isAuthenticatingWithMSAL = false;
      this.userScopes = [];
      this.hasHelpdeskAccess = false;
      this.basicInfo = {};

      // Step 3: Force clear MSAL instance memory state
      const msalInstance = this.msalService.instance;

      // Just clear the active account - MSAL handles the rest
      try {
        msalInstance.setActiveAccount(null);
        this.msalAccountInfo$$.next(undefined);
        console.log("✅ Active account cleared from MSAL instance");
      } catch (error) {
        console.warn("⚠️ Failed to clear active account:", error);
      }

      // Step 4: Force clear browser storage again to be sure
      this.clearAllMSALStorage();

      // Step 5: Reset interaction state
      this.clearInteractionState();

      console.log(
        "✅ Emergency reset completed. Please refresh the page and try login again."
      );

      // Optional: Auto-refresh after reset
      setTimeout(() => {
        if (
          confirm(
            "Emergency reset completed. Would you like to refresh the page?"
          )
        ) {
          window.location.reload();
        }
      }, 1000);
    } catch (error) {
      console.error("❌ Emergency reset failed:", error);
      // Fallback: suggest manual browser actions
      console.log(
        "💡 Please try manually: clear browser cache, close all tabs, and restart browser"
      );
    }
  }

  /**
   * Enhanced interaction state clearing with more aggressive approach
   */
  logout() {
    const currentAccount = this.msalService.instance.getActiveAccount();
    const logoutHint = currentAccount?.idTokenClaims?.login_hint;

    // Clear local state
    this.userScopes = [];
    this.hasHelpdeskAccess = false;
    this.basicInfo = {};

    this.msalService.logoutPopup({
      logoutHint: logoutHint,
      mainWindowRedirectUri: "/",
    });
  }

  /**
   * Get current authentication status and permissions
   */
  /**
   * Simple authentication check
   */
  isAuthenticated(): boolean {
    const accounts = this.msalService.instance.getAllAccounts();
    return accounts.length > 0;
  }

  /**
   * Detect if third-party cookies are likely blocked
   */
  private detectThirdPartyCookiesBlocked(): boolean {
    try {
      // Check if cookies are enabled
      const testCookie = 'msal_test_cookie_' + Date.now();
      document.cookie = testCookie + '=1; SameSite=None; Secure';
      const cookiesEnabled = document.cookie.includes(testCookie);
      document.cookie = testCookie + '=; Max-Age=-99999999;';

      if (!cookiesEnabled) {
        console.warn('⚠️ Cookies appear to be disabled in the browser');
        return true;
      }

      // Check browser for common privacy features that block third-party cookies
      const userAgent = navigator.userAgent;
      const commonBrowsers = ['Chrome', 'Firefox', 'Safari', 'Edge', 'Opera'];
      const browserName = commonBrowsers.find(browser => userAgent.includes(browser)) || 'Unknown';

      console.log(`🔍 Browser detection: ${browserName}`);

      // Some browsers block third-party cookies by default
      // We can detect this by trying to access cross-origin storage
      try {
        localStorage.setItem('msal_test', 'test');
        localStorage.removeItem('msal_test');
        return false;
      } catch (e) {
        console.warn('⚠️ Storage access restricted - third-party cookies may be blocked');
        return true;
      }
    } catch (e) {
      console.warn('⚠️ Could not detect cookie settings:', e);
      return false;
    }
  }

  /**
   * Get current access token for debugging
   */
  getCurrentTokens(): Observable<{
    accessToken: string | null;
    idToken: string | null;
    account: AccountInfo | null;
    tokenExpiry: string | null;
  }> {
    const account = this.msalService.instance.getActiveAccount();

    if (!account) {
      console.warn("❌ No active account found for token acquisition");
      return of({
        accessToken: null,
        idToken: null,
        account: null,
        tokenExpiry: null,
      });
    }

    const silentRequest: SilentRequest = {
      scopes: environment.azureAuthenticationConfig.scopes.all,
      account: account,
      // Add prompt none to avoid unnecessary interactive prompts
      prompt: 'none' as any,
    };

    return new Observable((observer) => {
      this.msalService.acquireTokenSilent(silentRequest).subscribe({
        next: (result) => {
          console.log('🎯 Token acquired successfully:', {
            scopes: result.scopes,
            expiresOn: result.expiresOn,
            account: result.account?.username
          });

          observer.next({
            accessToken: result.accessToken,
            idToken: result.idToken || null,
            account: result.account,
            tokenExpiry: result.expiresOn?.toISOString() || null,
          });
          observer.complete();
        },
        error: (error) => {
          console.error("❌ Could not get current tokens:", error);

          // Handle iframe timeout error specifically
          if (error.errorCode === 'monitor_window_timeout' ||
              error.errorCode === 'monitor_window_timeout: Token acquisition in iframe failed due to timeout' ||
              error.errorMessage?.includes('monitor_window_timeout')) {
            console.log("⏰ Iframe timeout detected");

            // Check if third-party cookies are blocked
            const cookiesBlocked = this.detectThirdPartyCookiesBlocked();

            if (cookiesBlocked) {
              console.warn('⚠️ Third-party cookies appear to be blocked');
              console.log('💡 To fix this issue:');
              console.log('   1. Enable third-party cookies in your browser settings for login.microsoftonline.com');
              console.log('   2. Or add this site to your browser\'s allowed exceptions');
              console.log('   3. Chrome: Settings > Privacy > Third-party cookies > "Sites that can use third-party cookies" > Add');
              console.log('   4. Firefox: Settings > Privacy & Security > Cookies > "Manage exceptions"');
              console.log('   5. Edge: Settings > Cookies > "Manage and delete cookies" > "Allow"');

              // Emit event to show cookie warning modal
              this.cookieWarningNeeded.emit();
            }

            console.log('🔄 Automatically triggering interactive authentication to refresh your session...');

            // Automatically trigger interactive login to resolve the issue
            setTimeout(() => {
              console.log('🚀 Initiating interactive login due to iframe timeout...');
              this.login();
            }, 500);

            observer.next({
              accessToken: null,
              idToken: account?.idToken || null,
              account: account,
              tokenExpiry: null,
            });
            observer.complete();
            return;
          }

          // Handle interaction required errors - session expired
          if (error.errorCode === 'interaction_required' ||
              error.errorMessage?.includes('AADSTS160021') ||
              error.errorMessage?.includes('user session which does not exist') ||
              error.errorMessage?.includes('AADSTS50058')) {
            console.log("🔄 Session expired or invalid, triggering interactive authentication...");

            // Clear stale authentication state
            this.emergencyAuthReset();

            // Trigger interactive login after reset
            setTimeout(() => {
              console.log("🚀 Initiating fresh interactive login...");
              this.login();
            }, 1000);

            observer.next({
              accessToken: null,
              idToken: account?.idToken || null,
              account: account,
              tokenExpiry: null,
            });
            observer.complete();
            return;
          }

          // Handle generic errors
          console.warn("⚠️ Token acquisition failed with unknown error:", error.errorCode, error.errorMessage);

          // For any other error, try interactive login as fallback
          console.log('🔄 Attempting interactive login as fallback...');
          setTimeout(() => {
            this.login();
          }, 500);

          observer.next({
            accessToken: null,
            idToken: account?.idToken || null,
            account: account,
            tokenExpiry: null,
          });
          observer.complete();
        },
      });
    });
  }

  /**
   * Get current authentication status and permissions
   */
  getAuthStatus(): Observable<{
    isAuthenticated: boolean;
    hasHelpdeskAccess: boolean;
    scopes: string[];
  }> {
    const account = this.msalService.instance.getActiveAccount();

    if (!account) {
      return of({
        isAuthenticated: false,
        hasHelpdeskAccess: false,
        scopes: [],
      });
    }

    // Try to get current token to check scopes
    const silentRequest: SilentRequest = {
      scopes: environment.azureAuthenticationConfig.scopes.all,
      account: account,
    };

    return new Observable((observer) => {
      this.msalService.acquireTokenSilent(silentRequest).subscribe({
        next: (result) => {
          this.userScopes = result.scopes || [];
          this.hasHelpdeskAccess = this.userScopes.some(
            (scope) =>
              scope.includes("helpdesk") || scope.includes("access_as_user")
          );

          observer.next({
            isAuthenticated: true,
            hasHelpdeskAccess: this.hasHelpdeskAccess,
            scopes: this.userScopes,
          });
          observer.complete();
        },
        error: (error) => {
          console.warn("Could not get current token scopes:", error);
          observer.next({
            isAuthenticated: true,
            hasHelpdeskAccess: false,
            scopes: [],
          });
          observer.complete();
        },
      });
    });
  }
}
